import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCachedTechnicalIndicators,
    getAvailableSymbols,
    setSelectedSymbol,
    setSelectedTimeframe,
    setApiKey,
    loadApiKeyFromStorage,
    clearError,
    selectTechnicalIndicators,
    selectAvailableSymbols,
    selectSelectedSymbol,
    selectSelectedTimeframe,
    selectApiKey,
    selectForexLoading,
    selectForexError,
    selectResponseTime,
    selectDataAge,
    selectDataSource,
    selectUserInfo
} from '../redux/slices/forexSlice';

const ForexData = () => {
    const dispatch = useDispatch();
    const symbolData = useSelector(selectTechnicalIndicators);
    const availableSymbols = useSelector(selectAvailableSymbols);
    const selectedSymbol = useSelector(selectSelectedSymbol);
    const selectedTimeframe = useSelector(selectSelectedTimeframe);
    const apiKey = useSelector(selectApiKey);
    const loading = useSelector(selectForexLoading);
    const error = useSelector(selectForexError);
    const responseTime = useSelector(selectResponseTime);
    const dataAge = useSelector(selectDataAge);
    const dataSource = useSelector(selectDataSource);
    const userInfo = useSelector(selectUserInfo);

    const [autoRefresh, setAutoRefresh] = useState(false); // Start with false until API key is provided
    const [showApiKey, setShowApiKey] = useState(false);

    // Available timeframes
    const availableTimeframes = [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' }
    ];

    // Load API key from localStorage on component mount
    useEffect(() => {
        dispatch(loadApiKeyFromStorage());
    }, [dispatch]);

    // Load available symbols when API key is available
    useEffect(() => {
        if (apiKey) {
            dispatch(getAvailableSymbols({ apiKey }));
        }
    }, [dispatch, apiKey]);

    // Auto-refresh data every 5 seconds
    useEffect(() => {
        if (autoRefresh && selectedSymbol && apiKey) {
            const interval = setInterval(() => {
                dispatch(getCachedTechnicalIndicators({ 
                    symbol: selectedSymbol, 
                    timeframe: selectedTimeframe,
                    apiKey
                }));
            }, 5000);

            // Initial fetch
            dispatch(getCachedTechnicalIndicators({ 
                symbol: selectedSymbol, 
                timeframe: selectedTimeframe,
                apiKey
            }));

            return () => clearInterval(interval);
        }
    }, [dispatch, selectedSymbol, selectedTimeframe, apiKey, autoRefresh]);

    const handleSymbolChange = (symbol) => {
        dispatch(setSelectedSymbol(symbol));
    };

    const handleTimeframeChange = (timeframe) => {
        dispatch(setSelectedTimeframe(timeframe));
    };

    const handleApiKeyChange = (newApiKey) => {
        dispatch(setApiKey(newApiKey));
        if (newApiKey) {
            // Clear any previous errors
            dispatch(clearError());
        }
    };

    const handleManualRefresh = () => {
        if (!apiKey) {
            return;
        }
        dispatch(getCachedTechnicalIndicators({ 
            symbol: selectedSymbol, 
            timeframe: selectedTimeframe,
            apiKey
        }));
    };

    const isApiKeyValid = apiKey && apiKey.length > 0;

    return (
        <div className="container py-4">
            <div className="card shadow">
                <div className="card-header bg-success text-white">
                    <h3 className="mb-0">Real-Time Forex Technical Indicators</h3>
                    <small>Data updated every second in background</small>
                </div>
                
                <div className="card-body">
                    {/* API Key Input */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card border-primary">
                                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">API Configuration</h6>
                                    <button 
                                        className="btn btn-sm btn-outline-light"
                                        onClick={() => setShowApiKey(!showApiKey)}
                                    >
                                        {showApiKey ? 'Hide' : 'Show'} API Key
                                    </button>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-8">
                                            <label className="form-label fw-bold">API Key:</label>
                                            <input
                                                type={showApiKey ? "text" : "password"}
                                                className={`form-control ${!isApiKeyValid ? 'is-invalid' : (apiKey ? 'is-valid' : '')}`}
                                                placeholder="Enter your API key here..."
                                                value={apiKey}
                                                onChange={(e) => handleApiKeyChange(e.target.value)}
                                            />
                                            {!isApiKeyValid && (
                                                <div className="invalid-feedback">
                                                    API key is required to fetch data
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-4">
                                            {userInfo && (
                                                <div className="mt-4">
                                                    <small className="text-muted">
                                                        <strong>Plan:</strong> {userInfo.subscription_plan}<br/>
                                                        <strong>Remaining Calls:</strong> {userInfo.remaining_api_calls}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="alert alert-danger mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Error:</strong> {error}
                                </div>
                                <button 
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => dispatch(clearError())}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Performance Stats */}
                    {(responseTime || dataAge !== null || dataSource) && (
                        <div className="alert alert-info mb-4">
                            <div className="row">
                                <div className="col-md-3">
                                    {responseTime && (
                                        <span><strong>Response:</strong> {responseTime}ms</span>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    {dataAge !== null && (
                                        <span><strong>Data Age:</strong> {dataAge}s</span>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    {dataSource && (
                                        <span><strong>Source:</strong> {dataSource}</span>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <span><strong>Timeframe:</strong> {selectedTimeframe}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Controls */}
                    <div className="row mb-4">
                        {/* Symbol Selection */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Select Symbol:</label>
                            <select
                                className="form-select"
                                value={selectedSymbol}
                                onChange={(e) => handleSymbolChange(e.target.value)}
                                disabled={!isApiKeyValid}
                            >
                                {availableSymbols.map(symbol => (
                                    <option key={symbol} value={symbol}>{symbol}</option>
                                ))}
                            </select>
                        </div>

                        {/* Timeframe Selection */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Select Timeframe:</label>
                            <select
                                className="form-select"
                                value={selectedTimeframe}
                                onChange={(e) => handleTimeframeChange(e.target.value)}
                                disabled={!isApiKeyValid}
                            >
                                {availableTimeframes.map(tf => (
                                    <option key={tf.value} value={tf.value}>{tf.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Controls */}
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Actions:</label>
                            <div className="d-flex gap-2 flex-wrap">
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleManualRefresh} 
                                    disabled={loading || !isApiKeyValid}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-clockwise me-2"></i>
                                            Refresh Now
                                        </>
                                    )}
                                </button>
                                
                                <div className="form-check d-flex align-items-center">
                                    <input 
                                        className="form-check-input me-2"
                                        type="checkbox" 
                                        checked={autoRefresh}
                                        onChange={(e) => setAutoRefresh(e.target.checked)}
                                        id="autoRefreshCheck"
                                        disabled={!isApiKeyValid}
                                    />
                                    <label className="form-check-label" htmlFor="autoRefreshCheck">
                                        Auto-refresh (5s)
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Available Symbols Display */}
                    {availableSymbols.length > 0 && (
                        <div className="mb-4">
                            <h6>Available Symbols ({availableSymbols.length}):</h6>
                            <div className="d-flex flex-wrap gap-2">
                                {availableSymbols.map(symbol => (
                                    <span 
                                        key={symbol} 
                                        className={`badge ${symbol === selectedSymbol ? 'bg-primary' : 'bg-secondary'}`}
                                        style={{ cursor: isApiKeyValid ? 'pointer' : 'not-allowed' }}
                                        onClick={() => isApiKeyValid && handleSymbolChange(symbol)}
                                    >
                                        {symbol}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Timeframe Display */}
                    <div className="mb-4">
                        <h6>Available Timeframes:</h6>
                        <div className="d-flex gap-2">
                            {availableTimeframes.map(tf => (
                                <span 
                                    key={tf.value} 
                                    className={`badge ${tf.value === selectedTimeframe ? 'bg-success' : 'bg-outline-secondary'}`}
                                    style={{ cursor: isApiKeyValid ? 'pointer' : 'not-allowed' }}
                                    onClick={() => isApiKeyValid && handleTimeframeChange(tf.value)}
                                >
                                    {tf.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Data Display */}
                    <div className="mt-4">
                        {!isApiKeyValid ? (
                            <div className="text-center py-5">
                                <div className="mb-3">
                                    <i className="bi bi-key-fill text-warning" style={{ fontSize: '3rem' }}></i>
                                </div>
                                <h5>API Key Required</h5>
                                <p className="text-muted">
                                    Please enter your API key above to start fetching forex data
                                </p>
                            </div>
                        ) : symbolData ? (
                            <div>
                                <div className="d-flex align-items-center mb-3">
                                    <h4 className="h5 mb-0">
                                        Technical Indicators for {selectedSymbol} ({selectedTimeframe})
                                    </h4>
                                    <span className="badge bg-success ms-2">Live Data</span>
                                </div>
                                <div className="bg-light p-3 rounded border" style={{ maxHeight: '500px', overflow: 'auto' }}>
                                    <pre className="mb-0" style={{ fontSize: '0.875rem' }}>
                                        {JSON.stringify(symbolData, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5>Loading {selectedTimeframe} data for {selectedSymbol}...</h5>
                                <p className="text-muted">
                                    Please wait while we fetch the latest data from our real-time stream
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForexData;